package example.shadcn_kotlin.ui.pages.docs.components.dropdown_menu

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*
import example.shadcn_kotlin.ui.components.*
import example.shadcn_kotlin.ui.pages.components.demo.DropdownMenuDemo
import example.shadcn_kotlin.ui.pages.components.demo.DropdownMenuCheckboxes
import example.shadcn_kotlin.ui.pages.components.demo.DropdownMenuRadioGroupDemo

@Page
@Composable
fun IndexPage() {
    CompositionLocalProvider(LocalMarkdownContext provides MarkdownContext("docs/components/dropdown-menu/index.md", mapOf("title" to listOf("Dropdown Menu"), "description" to listOf("Displays a menu to the user — such as a set of actions or functions — triggered by a button."), "radix" to listOf(), "link" to listOf("https://www.radix-ui.com/docs/primitives/components/dropdown-menu"), "api" to listOf("https://www.radix-ui.com/docs/primitives/components/dropdown-menu#api-reference")))) {
        example.shadcn_kotlin.ui.components.layouts.MDLayout {
            ComponentPreview("""import lucide_react.*
import radix_ui.components.DropdownMenuProps
import react.FC
import react.dom.html.ReactHTML.span
import shadcn.ui.components.*
import web.cssom.ClassName

val DropdownMenuDemo = FC<DropdownMenuProps> {
    DropdownMenu {
        DropdownMenuTrigger {
            asChild = true
            Button {
                variant = "outline"
                +"Open"
            }
        }
        DropdownMenuContent {
            className = ClassName("w-56")
            DropdownMenuLabel {
                +"My Account"
            }
            DropdownMenuSeparator {}
            DropdownMenuGroup {
                DropdownMenuItem {
                    User {
                        className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"Profile"
                    }
                    DropdownMenuShortcut {
                        +"⇧⌘P"
                    }
                }
                DropdownMenuItem {
                    CreditCard {
                        className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"Billing"
                    }
                    DropdownMenuShortcut {
                        +"⌘B"
                    }
                }
                DropdownMenuItem {
                    Settings {
                        className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"Settings"
                    }
                    DropdownMenuShortcut {
                        +"⌘S"
                    }
                }
                DropdownMenuItem {
                    Keyboard {
                        className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"Keyboard shortcuts"
                    }
                    DropdownMenuShortcut {
                        +"⌘K"
                    }
                }
            }
            DropdownMenuSeparator {}

            DropdownMenuGroup {
                DropdownMenuItem {
                    Users {
                        className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"Team"
                    }
                }
                DropdownMenuSub {
                    DropdownMenuSubTrigger {
                        UserPlus { className = ClassName("mr-2 h-4 w-4") }
                        span {
                            +"Invite users"
                        }
                    }
                    DropdownMenuPortal {
                        DropdownMenuSubContent {
                            DropdownMenuItem {
                                Mail {
                                    className = ClassName("mr-2 h-4 w-4")
                                }
                                span {
                                    +"Email"
                                }
                            }
                            DropdownMenuItem {
                                MessageSquare {
                                    className = ClassName("mr-2 h-4 w-4")
                                }
                                span {
                                    +"Message"
                                }
                            }
                            DropdownMenuSeparator {}
                            DropdownMenuItem {
                                PlusCircle {
                                    className = ClassName("mr-2 h-4 w-4")
                                }
                                span {
                                    +"More..."
                                }
                            }
                        }
                    }
                }
                DropdownMenuItem {
                    Plus {
                       className = ClassName("mr-2 h-4 w-4")
                    }
                    span {
                        +"New Team"
                    }
                    DropdownMenuShortcut {
                        +"⌘+T"
                    }
                }
            }
            DropdownMenuSeparator {}
            DropdownMenuItem {
                Github {
                    className = ClassName("mr-2 h-4 w-4")
                }
                span {
                    +"Github"
                }
            }
            DropdownMenuItem {
                LifeBuoy {
                    className = ClassName("mr-2 h-4 w-4")
                }
                span {
                    +"Support"
                }
            }
            DropdownMenuItem {
                disabled = true
                Cloud {
                    className = ClassName("mr-2 h-4 w-4")
                }
                span {
                    +"API"
                }
            }
            DropdownMenuSeparator {}
            DropdownMenuItem {
                LogOut {
                    className = ClassName("mr-2 h-4 w-4")
                }
                span {
                    +"Log out"
                }
                DropdownMenuShortcut {
                    +"⇧⌘Q"
                }
            }
        }
    }
}"""){ 
 DropdownMenuDemo {} 
 }
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("usage")
 }) {
                org.jetbrains.compose.web.dom.Text("Usage")
            }
            CodeBlock("""import shadcn.ui.components.DropdownMenu
import shadcn.ui.components.DropdownMenuContent
import shadcn.ui.components.DropdownMenuItem
import shadcn.ui.components.DropdownMenuLabel
import shadcn.ui.components.DropdownMenuSeparator
import shadcn.ui.components.DropdownMenuTrigger
""", lang= "kotlin", highlight= "")
            CodeBlock("""DropdownMenu {
    DropdownMenuTrigger { + "Open" }
    DropdownMenuContent {
        DropdownMenuLabel { + "My Account" }
        DropdownMenuSeparator {}
        DropdownMenuItem { + "Profile" }
        DropdownMenuItem { + "Billing" }
        DropdownMenuItem { + "Team" }
        DropdownMenuItem { + "Subscription" }
    }
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("examples")
 }) {
                org.jetbrains.compose.web.dom.Text("Examples")
            }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("checkboxes")
 }) {
                org.jetbrains.compose.web.dom.Text("Checkboxes")
            }
            ComponentPreview("""import react.FC
import react.Props
import react.useState
import shadcn.ui.components.*
import web.cssom.ClassName

val DropdownMenuCheckboxes = FC<Props> {
    val (showStatusBar, setShowStatusBar) = useState(true)
    val (showActivityBar, setShowActivityBar) = useState(false)
    val (showPanel, setShowPanel) = useState(false)

    DropdownMenu {
        DropdownMenuTrigger {
            asChild = true
            Button {
                variant = "outline"
                +"Open"
            }
        }
        DropdownMenuContent {
            className = ClassName("w-56")
            DropdownMenuLabel { +"Appearance" }
            DropdownMenuSeparator {}
            DropdownMenuCheckboxItem {
                checked = showStatusBar
                onCheckedChange = { setShowStatusBar(it) }
                +"Status Bar"
            }
            DropdownMenuCheckboxItem {
                checked = showActivityBar
                onCheckedChange = { setShowActivityBar(it) }
                disabled = true
                +"Activity Bar"
            }
            DropdownMenuCheckboxItem {
                checked = showPanel
                onCheckedChange = { setShowPanel(it) }
                +"Panel"
            }
        }
    }

}"""){ 
 DropdownMenuCheckboxes {} 
 }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("radio-group")
 }) {
                org.jetbrains.compose.web.dom.Text("Radio Group")
            }
            ComponentPreview("""import react.FC
import react.Props
import react.useState
import shadcn.ui.components.*
import web.cssom.ClassName

val DropdownMenuRadioGroupDemo = FC<Props> {
    val (position, setPosition) = useState("bottom")
    DropdownMenu {
        DropdownMenuTrigger {
            asChild = true
            Button {
                variant = "outline"
                +"Open"
            }
        }
        DropdownMenuContent {
            className = ClassName("w-56")
            DropdownMenuLabel { +"Panel Position" }
            DropdownMenuSeparator {}
            DropdownMenuRadioGroup {
                value = position
                onValueChange = { setPosition(it.toString()) }
                DropdownMenuRadioItem {
                    value = "top"
                    +"Top"
                }
                DropdownMenuRadioItem {
                    value = "bottom"
                    +"Bottom"
                }
                DropdownMenuRadioItem {
                    value = "right"
                    +"Right"
                }
            }
        }
    }
}

"""){ 
 DropdownMenuRadioGroupDemo {} 
 }
        }
    }
}
